import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { Overline, StatusBadge } from "@/components/ui-bits";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { TrendingUp, TrendingDown, DollarSign, Wallet, Gauge, Percent, Bot, Loader2, Trophy, Flame, Check } from "lucide-react";

const RANGES = [
  { label: "7D", value: 7 },
  { label: "30D", value: 30 },
  { label: "90D", value: 90 },
];

const ROLE_COLOR = {
  builder: "text-cyan-400", seller: "text-emerald-400", support: "text-amber-400", api_operator: "text-violet-400",
};

export default function ROIEconomics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (d = days) => {
    api.get(`/analytics/roi?days=${d}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { setLoading(true); load(days); /* eslint-disable-next-line */ }, [days]);
  useRealtime(() => load());

  const roiPositive = (data?.net ?? 0) >= 0;

  return (
    <div className="space-y-6" data-testid="roi-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Overline>Unit Economics & Return on Spend</Overline>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">ROI & Economics</h1>
          <p className="text-sm text-slate-400 mt-1">Every dollar your fleet spent on AI &amp; ops vs. the revenue it earned back.</p>
        </div>
        <div className="flex rounded-md border border-slate-700 overflow-hidden" data-testid="roi-range-toggle">
          {RANGES.map((r) => (
            <button
              key={r.value}
              data-testid={`roi-range-${r.value}`}
              onClick={() => setDays(r.value)}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${days === r.value ? "bg-cyan-500 text-[#080B11] font-bold" : "text-slate-400 hover:bg-slate-800"}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="hud-card rounded-lg p-16 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi testid="roi-kpi-revenue" icon={DollarSign} label="Revenue Earned" value={money(data.revenue)}
              accent="text-emerald-400" sub={`${data.revenue_events} earning events`} line="#10B981" />
            <Kpi testid="roi-kpi-spend" icon={Wallet} label="AI & Ops Spend" value={money(data.spend)}
              accent="text-amber-400" sub={`${data.spend_events} spend events`} line="#F59E0B" />
            <Kpi testid="roi-kpi-net" icon={roiPositive ? TrendingUp : TrendingDown} label="Net Profit" value={money(data.net)}
              accent={roiPositive ? "text-cyan-400" : "text-red-400"} sub={roiPositive ? "Fleet is profitable" : "Running at a loss"} line={roiPositive ? "#06B6D4" : "#EF4444"} />
            <Kpi testid="roi-kpi-multiple" icon={Gauge} label="Return Multiple"
              value={data.roi_multiple != null ? `${data.roi_multiple}×` : "—"}
              accent="text-violet-400"
              sub={data.margin != null ? `${data.margin}% margin` : "No revenue yet"} line="#8B5CF6" />
          </div>

          {/* Chart */}
          <div className="hud-card rounded-lg p-5" data-testid="roi-chart-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Percent className="w-4 h-4 text-cyan-400" />
                <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Revenue vs. Spend — last {data.days} days</span>
              </div>
            </div>
            {data.series.length === 0 ? (
              <EmptyChart />
            ) : (
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <ComposedChart data={data.series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                    <XAxis dataKey="day" tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} tickFormatter={(d) => d.slice(5)} stroke="#334155" />
                    <YAxis tick={{ fill: "#64748B", fontSize: 10, fontFamily: "monospace" }} stroke="#334155" tickFormatter={(v) => `$${v}`} width={54} />
                    <Tooltip
                      contentStyle={{ background: "#0E131F", border: "1px solid #334155", borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: "#94A3B8", fontFamily: "monospace" }}
                      formatter={(v, n) => [money(v), n]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
                    <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10B981" strokeWidth={2} fill="url(#revFill)" />
                    <Area type="monotone" dataKey="spend" name="Spend" stroke="#F59E0B" strokeWidth={2} fill="url(#spendFill)" />
                    <Line type="monotone" dataKey="net" name="Net" stroke="#06B6D4" strokeWidth={2} dot={false} strokeDasharray="4 3" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Fleet leaderboard */}
          {data.agents.length > 0 && (
            <div className="hud-card rounded-lg p-5" data-testid="roi-leaderboard">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-4 h-4 text-amber-400" />
                <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Fleet Leaderboard — top earners</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[...data.agents].sort((a, b) => b.net - a.net).slice(0, 3).map((a, i) => {
                  const medal = ["#F5C518", "#C0C7D1", "#CD7F32"][i];
                  return (
                    <div key={a.agent_id} className="rounded-md border border-slate-700 bg-[#080B11] p-4 relative overflow-hidden" data-testid={`leaderboard-${a.agent_id}`}>
                      <div className="absolute top-3 right-3 font-display font-black text-2xl" style={{ color: medal }}>#{i + 1}</div>
                      <div className="font-medium text-slate-100 pr-8 truncate">{a.name}{a.badges?.includes("streak_7") && <span title="Hot Streak — 7+ profitable days" className="ml-1">🔥</span>}</div>
                      <div className={`font-display font-extrabold text-xl tabular-nums mt-1 ${a.net >= 0 ? "text-cyan-400" : "text-red-400"}`}>{money(a.net)}</div>
                      <div className="flex items-center gap-3 mt-2 text-[10px] font-mono text-slate-500">
                        <span className="text-emerald-400">{money(a.revenue)} earned</span>
                        {a.conversion_rate != null && <span className="text-violet-400">{a.conversion_rate}% convert</span>}
                        {a.streak_days > 0 && <span className="flex items-center gap-0.5 text-amber-400" data-testid={`streak-${a.agent_id}`}><Flame className="w-3 h-3" />{a.streak_days}d</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Campaign attribution */}
          {data.campaigns?.length > 0 && (
            <div className="hud-card rounded-lg p-5" data-testid="roi-campaigns">
              <div className="flex items-center gap-2 mb-1">
                <Percent className="w-4 h-4 text-cyan-400" />
                <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Conversion by ad campaign</span>
              </div>
              <p className="text-[11px] text-slate-500 mb-3">Enter what you spent on each campaign to see true net profit and cost-per-close.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                      <th className="text-left font-medium px-2 py-2">Campaign</th>
                      <th className="text-right font-medium px-2 py-2">Revenue</th>
                      <th className="text-right font-medium px-2 py-2">Closes</th>
                      <th className="text-right font-medium px-2 py-2">Ad Spend</th>
                      <th className="text-right font-medium px-2 py-2">Net Profit</th>
                      <th className="text-right font-medium px-2 py-2">Cost / Close</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.campaigns.map((c) => (
                      <CampaignRow key={c.campaign} c={c} onSaved={() => load()} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Per-agent table */}
          <div className="hud-card rounded-lg overflow-hidden" data-testid="roi-agent-table">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-800">
              <Bot className="w-4 h-4 text-cyan-400" />
              <span className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Per-Agent Return</span>
            </div>
            {data.agents.length === 0 ? (
              <div className="p-10 text-center text-slate-500 text-sm">No agent revenue or spend in this window yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                      <th className="text-left font-medium px-5 py-2.5">Agent</th>
                      <th className="text-right font-medium px-3 py-2.5">Revenue</th>
                      <th className="text-right font-medium px-3 py-2.5">Spend</th>
                      <th className="text-right font-medium px-3 py-2.5">Net</th>
                      <th className="text-right font-medium px-3 py-2.5">ROI</th>
                      <th className="text-right font-medium px-3 py-2.5" title="Paid orders / conversations">Convert</th>
                      <th className="text-left font-medium px-5 py-2.5 w-[22%]">Efficiency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.agents.map((a) => {
                      const maxNet = Math.max(...data.agents.map((x) => Math.abs(x.net)), 1);
                      const pct = Math.min(100, (Math.abs(a.net) / maxNet) * 100);
                      const pos = a.net >= 0;
                      return (
                        <tr key={a.agent_id} className="border-b border-slate-800/60 hover:bg-slate-800/20" data-testid={`roi-agent-row-${a.agent_id}`}>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-200">{a.name}</span>
                              {a.role && <span className={`font-mono text-[9px] uppercase ${ROLE_COLOR[a.role] || "text-slate-400"}`}>{a.role}</span>}
                              {a.streak_days >= 2 && <span className="flex items-center gap-0.5 font-mono text-[9px] text-amber-400" title={`${a.streak_days}-day profitable streak`}><Flame className="w-3 h-3" />{a.streak_days}d</span>}
                              {a.badges?.includes("streak_7") && <span className="font-mono text-[9px] uppercase text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5" title="Hot Streak reward unlocked">🔥 Reward</span>}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-right font-mono tabular-nums text-emerald-400">{money(a.revenue)}</td>
                          <td className="px-3 py-3 text-right font-mono tabular-nums text-amber-400">{money(a.spend)}</td>
                          <td className={`px-3 py-3 text-right font-mono tabular-nums font-bold ${pos ? "text-cyan-400" : "text-red-400"}`}>{money(a.net)}</td>
                          <td className="px-3 py-3 text-right font-mono tabular-nums text-violet-400">{a.roi_multiple != null ? `${a.roi_multiple}×` : "—"}</td>
                          <td className="px-3 py-3 text-right font-mono tabular-nums" data-testid={`roi-convert-${a.agent_id}`}>
                            {a.conversion_rate != null
                              ? <span className={a.conversion_rate > 0 ? "text-emerald-400" : "text-slate-500"}>{a.conversion_rate}%<span className="text-slate-600 text-[10px]"> ({a.paid_orders}/{a.conversations})</span></span>
                              : <span className="text-slate-600">—</span>}
                          </td>
                          <td className="px-5 py-3">
                            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: pos ? "#06B6D4" : "#EF4444" }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function CampaignRow({ c, onSaved }) {
  const [spend, setSpend] = useState(c.ad_spend != null ? String(c.ad_spend) : "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/analytics/campaign-spend", { campaign: c.campaign, amount: Number(spend || 0) });
      toast.success(`Ad spend saved for ${c.campaign}`);
      onSaved();
    } catch {
      toast.error("Could not save ad spend");
    } finally {
      setSaving(false);
    }
  };
  const net = Number(spend || 0) > 0 ? c.revenue - Number(spend) : c.net_profit;
  const cpc = c.closes && Number(spend || 0) > 0 ? Number(spend) / c.closes : c.cost_per_close;
  return (
    <tr className="border-b border-slate-800/60 last:border-0" data-testid={`campaign-${c.campaign}`}>
      <td className="px-2 py-2.5 font-mono text-xs text-slate-300">{c.campaign}</td>
      <td className="px-2 py-2.5 text-right font-mono tabular-nums text-emerald-400">{money(c.revenue)}</td>
      <td className="px-2 py-2.5 text-right font-mono tabular-nums text-slate-400">{c.closes}</td>
      <td className="px-2 py-2.5 text-right">
        <div className="flex items-center justify-end gap-1">
          <span className="text-slate-600 text-xs">$</span>
          <input
            data-testid={`campaign-spend-input-${c.campaign}`}
            type="number" min="0" step="0.01" value={spend}
            onChange={(e) => setSpend(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            placeholder="0"
            className="w-20 bg-slate-900/60 border border-slate-700 rounded px-2 py-1 text-right font-mono text-xs text-slate-200 focus:border-cyan-500/60 focus:outline-none"
          />
          <button
            data-testid={`campaign-spend-save-${c.campaign}`}
            onClick={save} disabled={saving}
            className="p-1 rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors disabled:opacity-50"
            title="Save ad spend">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          </button>
        </div>
      </td>
      <td className={`px-2 py-2.5 text-right font-mono tabular-nums ${net >= 0 ? "text-emerald-400" : "text-red-400"}`} data-testid={`campaign-net-${c.campaign}`}>{money(net)}</td>
      <td className="px-2 py-2.5 text-right font-mono tabular-nums text-slate-300" data-testid={`campaign-cpc-${c.campaign}`}>{cpc != null ? money(cpc) : <span className="text-slate-600">—</span>}</td>
    </tr>
  );
}

function Kpi({ icon: Icon, label, value, accent, sub, line, testid }) {  return (
    <div className="hud-card accent-top rounded-lg p-5" style={{ "--accent-line": line }} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`w-4 h-4 ${accent}`} />
      </div>
      <div className={`font-display font-extrabold text-2xl tabular-nums mt-2 ${accent}`}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-1">{sub}</div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-[300px] flex flex-col items-center justify-center text-center">
      <Percent className="w-8 h-8 text-slate-700" />
      <p className="mt-3 text-sm text-slate-500 max-w-sm">No revenue or spend recorded in this window yet. Once your agents start earning and spending, the trend appears here.</p>
    </div>
  );
}
